import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, Globe2, ReceiptText, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import { missionSiteFallback } from "../data/missionContent";
import PageBanner from "../components/PageBanner";

const reportIconMap = {
  FileText,
  Globe2,
  ReceiptText,
  ShieldCheck,
};

const getConfiguredArray = (value, fallback = []) =>
  Array.isArray(value) && value.length ? value : fallback;

const getConfiguredText = (value, fallback = "") =>
  typeof value === "string" && value.trim() ? value : fallback;

const getConfiguredIcon = (iconName, fallbackIcon) => reportIconMap[iconName] || fallbackIcon;

const Reports = () => {
  const { t } = useTranslation();
  const { siteConfig, region } = useRegionalExperience();
  const brand = { ...missionSiteFallback.brand, ...(siteConfig.brand || {}) };
  const reportsPage = siteConfig.reportsPage || {};
  const hero = reportsPage.hero || {};
  const annualReports = reportsPage.annualReports || {};
  const confidence = reportsPage.confidence || {};
  const cta = reportsPage.cta || {};
  const transparency = { ...missionSiteFallback.transparency, ...(siteConfig.transparency || {}) };
  const reports = getConfiguredArray(
    transparency.reports,
    missionSiteFallback.transparency.reports
  );
  const programs = getConfiguredArray(
    siteConfig.programPillars,
    missionSiteFallback.programPillars
  );
  const homepageAdditions = getConfiguredArray(
    siteConfig.homepageAdditions,
    missionSiteFallback.homepageAdditions
  );
  const programAreas = programs
    .slice(0, 5)
    .map((program) => program.eyebrow || program.title)
    .filter(Boolean)
    .join(", ");
  const confidenceSource = getConfiguredArray(confidence.cards, homepageAdditions);
  const confidenceFallback = [
    {
      icon: "ReceiptText",
      title: t("reports.confidence.cards.donationClarity.title"),
      text: t("reports.confidence.cards.donationClarity.text", { regionLabel: region.label, paymentLabel: region.paymentLabel }),
    },
    {
      icon: "Globe2",
      title: t("reports.confidence.cards.globalSupport.title"),
      text: t("reports.confidence.cards.globalSupport.text", { shortName: brand.shortName }),
    },
    {
      icon: "ShieldCheck",
      title: t("reports.confidence.cards.responsibleComm.title"),
      text: transparency.description,
    },
  ];
  const confidenceCards = getConfiguredArray(confidenceSource, confidenceFallback)
    .slice(0, 3)
    .map((item, index) => ({
      icon: getConfiguredIcon(
        item.icon,
        getConfiguredIcon(confidenceFallback[index]?.icon, ShieldCheck)
      ),
      title: item.title || confidenceFallback[index]?.title,
      text: item.text || item.description || confidenceFallback[index]?.text,
    }));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="overflow-x-hidden bg-[#fffaf5] text-[#14201f]">
      <PageBanner {...(siteConfig.reportsPage?.banner || {})} />

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
                {getConfiguredText(annualReports.eyebrow, t("impact.trustChecks.annualReports.title"))}
              </p>
              <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                {getConfiguredText(
                  annualReports.title,
                  t("reports.annualReports.title", { shortName: brand.shortName })
                )}
              </h2>
              <p className="mt-6 text-base leading-8 text-[#52615f] md:text-lg">
                {getConfiguredText(
                  annualReports.description,
                  t("reports.annualReports.description", {
                    programAreas:
                      programAreas ||
                      t("reports.annualReports.descriptionFallbackAreas"),
                  })
                )}
              </p>
            </div>
            <div className="grid gap-4">
              {reports.map((report) => (
                <a
                  key={report.label}
                  href={report.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col gap-5 rounded-lg border border-[#dfe8e6] bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f8efe5] text-[#9d1f1c]">
                      <FileText size={24} />
                    </span>
                    <div>
                      <h3 className="text-xl font-semibold text-[#14201f]">{report.label}</h3>
                      <p className="mt-2 text-sm leading-6 text-[#52615f]">{report.note}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#9d1f1c]">
                    {t("reports.download")}
                    <Download size={17} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#9d1f1c]">
              {getConfiguredText(confidence.eyebrow, t("reports.confidence.eyebrow"))}
            </p>
            <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
              {getConfiguredText(
                confidence.title,
                t("reports.confidence.title")
              )}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {confidenceCards.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-7">
                <Icon className="h-8 w-8 text-[#9d1f1c]" />
                <h3 className="mt-6 text-2xl font-semibold text-[#14201f]">{title}</h3>
                <p className="mt-4 text-sm leading-7 text-[#52615f]">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg bg-[linear-gradient(135deg,#2a120d_0%,#4b2418_54%,#7f1916_100%)] p-8 text-white md:p-12">
            <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-center">
              <div>
                <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                  {getConfiguredText(
                    cta.title,
                    t("reports.cta.title")
                  )}
                </h2>
                <p className="mt-5 text-base leading-8 text-white/75">
                  {getConfiguredText(
                    cta.description,
                    t("reports.cta.description", {
                      transparencyDescription: transparency.description,
                      shortName: brand.shortName,
                    })
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <Link
                  to="/impact"
                  className="inline-flex items-center justify-center gap-3 rounded-md border border-white/25 px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white hover:text-[#4b2418]"
                >
                  {getConfiguredText(cta.primaryLabel, t("reports.cta.primaryLabel"))}
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to={region.donationPath}
                  className="inline-flex items-center justify-center gap-3 rounded-md bg-[#f5c45e] px-6 py-4 text-sm font-bold uppercase tracking-[0.14em] text-[#201711] transition hover:bg-[#ffd77c]"
                >
                  {getConfiguredText(cta.secondaryLabel, region.paymentLabel)}
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Reports;
